import { useState, useEffect, useMemo, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import {
  Users, Briefcase, Clock, Calendar, Sun, FileText, Phone, Mail, 
  Settings, X, Check, Pencil, Save, FolderOpen, Upload, Download, Trash2, Plus,
  ChevronLeft, ChevronRight
} from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { PreferenzeTurniDialog, ContrattoDialog, DocumentiDialog } from "../../components/schede-personale/SchedePersonaleDialogs";
import { BustePagaDialog } from "../../components/schede-personale/BustePagaDialog";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { cn } from "../../lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIPI_CONTRATTO = [
  { value: "full_time", label: "Full Time", ore: 40, ferie: 26, color: "bg-emerald-500" },
  { value: "part_time_30", label: "Part Time 30h", ore: 30, ferie: 26, color: "bg-blue-500" },
  { value: "part_time_24", label: "Part Time 24h", ore: 24, ferie: 26, color: "bg-blue-500" },
  { value: "part_time_20", label: "Part Time 20h", ore: 20, ferie: 26, color: "bg-blue-500" },
  { value: "apprendistato", label: "Apprendistato", ore: 40, ferie: 26, color: "bg-amber-500" },
  { value: "stagionale", label: "Stagionale", ore: 40, ferie: 0, color: "bg-orange-500" },
  { value: "chiamata", label: "A Chiamata", ore: 0, ferie: 0, color: "bg-purple-500" },
];

const GIORNI_SHORT = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

const RUOLI_INFO = {
  cucina: { label: "Cucina", color: "bg-orange-500/15 text-orange-600", icon: "bg-orange-100 text-orange-600", dot: "bg-orange-500" },
  sala: { label: "Sala", color: "bg-blue-500/15 text-blue-600", icon: "bg-blue-100 text-blue-600", dot: "bg-blue-500" },
  laboratorio: { label: "Laboratorio", color: "bg-purple-500/15 text-purple-600", icon: "bg-purple-100 text-purple-600", dot: "bg-purple-500" },
};

const SchedePersonale = () => {
  const { getAuthHeaders, localeCorrente, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [operatori, setOperatori] = useState([]);
  const [fasce, setFasce] = useState([]);
  const [turniSettimana, setTurniSettimana] = useState([]);
  const [preferenze, setPreferenze] = useState({});
  
  // Dialog preferenze turni
  const [dialogPreferenze, setDialogPreferenze] = useState(false);
  const [operatoreSelezionato, setOperatoreSelezionato] = useState(null);
  const [prefForm, setPrefForm] = useState({});
  
  // Dialog contratto
  const [dialogContratto, setDialogContratto] = useState(false);
  const [contrattoForm, setContrattoForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Dialog documenti
  const [dialogDocumenti, setDialogDocumenti] = useState(false);
  const [documentiOperatore, setDocumentiOperatore] = useState([]);
  const [loadingDocumenti, setLoadingDocumenti] = useState(false);
  const [nuovoDocForm, setNuovoDocForm] = useState({ nome: "", data_documento: "", data_scadenza: "", note: "" });
  const [showNuovoDocForm, setShowNuovoDocForm] = useState(false);

  // Buste Paga
  const [dialogBustePaga, setDialogBustePaga] = useState(false);

  // Contratti allegati
  const [contrattiAllegati, setContrattiAllegati] = useState([]);
  const [loadingContratti, setLoadingContratti] = useState(false);

  // Navigazione settimane
  const [offsetSettimana, setOffsetSettimana] = useState(0);
  const [oreLavorate, setOreLavorate] = useState({});

  // Calcola settimana in base all'offset
  const settimanaCorrente = useMemo(() => {
    const oggi = new Date();
    oggi.setDate(oggi.getDate() + (offsetSettimana * 7));
    const giornoSettimana = oggi.getDay();
    const diff = giornoSettimana === 0 ? -6 : 1 - giornoSettimana;
    const lunedi = new Date(oggi);
    lunedi.setDate(oggi.getDate() + diff);
    const domenica = new Date(lunedi);
    domenica.setDate(lunedi.getDate() + 6);
    return {
      inizio: lunedi.toISOString().split("T")[0],
      fine: domenica.toISOString().split("T")[0],
      label: `${lunedi.getDate()}/${lunedi.getMonth()+1} - ${domenica.getDate()}/${domenica.getMonth()+1}`
    };
  }, [offsetSettimana]);

  const fetchData = useCallback(async () => {
    if (!localeCorrente?.id) return;
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const localeParam = `locale_id=${localeCorrente.id}`;
      
      const [operatoriRes, fasceRes, turniRes, prefRes, oreLavRes] = await Promise.all([
        axios.get(`${API}/personale/utenti-turni?${localeParam}`, { headers }),
        axios.get(`${API}/personale/fasce-turno?${localeParam}`, { headers }),
        axios.get(`${API}/personale/turni?${localeParam}&data_inizio=${settimanaCorrente.inizio}&data_fine=${settimanaCorrente.fine}`, { headers }),
        axios.get(`${API}/personale/preferenze-turni?${localeParam}`, { headers }),
        axios.get(`${API}/personale/presenze/ore-settimanali?${localeParam}&data_inizio=${settimanaCorrente.inizio}&data_fine=${settimanaCorrente.fine}`, { headers })
      ]);
      
      setOperatori(operatoriRes.data);
      setFasce(fasceRes.data.filter(f => f.attivo !== false));
      setTurniSettimana(turniRes.data);
      
      const prefMap = {};
      prefRes.data.forEach(p => prefMap[p.utente_id] = p);
      setPreferenze(prefMap);

      // Mappa ore lavorate per utente
      const oreLavMap = {};
      oreLavRes.data.forEach(o => oreLavMap[o.utente_id] = o.ore_lavorate);
      setOreLavorate(oreLavMap);
      
    } catch (error) {
      console.error("Errore caricamento:", error);
      toast.error("Errore nel caricamento dati");
    } finally {
      setLoading(false);
    }
  }, [localeCorrente?.id, token, settimanaCorrente]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Calcola ore settimanali per operatore
  const oreSettimanaliPerOperatore = useMemo(() => {
    const oreMap = {};
    
    const calcolaOreFascia = (fascia) => {
      try {
        const [hInizio, mInizio] = fascia.ora_inizio.split(":").map(Number);
        const [hFine, mFine] = fascia.ora_fine.split(":").map(Number);
        let diff = (hFine * 60 + mFine) - (hInizio * 60 + mInizio);
        if (diff < 0) diff += 24 * 60;
        return diff / 60;
      } catch {
        return 4;
      }
    };
    
    const oreFasce = {};
    fasce.forEach(f => {
      oreFasce[f.id] = calcolaOreFascia(f);
    });
    
    turniSettimana.forEach(t => {
      if (!oreMap[t.utente_id]) {
        oreMap[t.utente_id] = 0;
      }
      oreMap[t.utente_id] += oreFasce[t.fascia_turno_id] || 4;
    });
    
    return oreMap;
  }, [turniSettimana, fasce]);

  // ============ PREFERENZE TURNI ============
  
  const openPreferenze = (operatore) => {
    const pref = preferenze[operatore.id] || {
      giorni_riposo: 1,
      giorni_riposo_disponibili: [4, 5, 6],
      turni_esclusi: []
    };
    setOperatoreSelezionato(operatore);
    setPrefForm(pref);
    setDialogPreferenze(true);
  };

  const handleSavePreferenze = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/personale/preferenze-turni?locale_id=${localeCorrente.id}`, {
        utente_id: operatoreSelezionato.id,
        giorni_riposo: prefForm.giorni_riposo || 1,
        giorni_riposo_disponibili: prefForm.giorni_riposo_disponibili || [4, 5, 6],
        turni_esclusi: prefForm.turni_esclusi || []
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Preferenze turni salvate");
      setDialogPreferenze(false);
      fetchData();
    } catch (error) {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const toggleGiornoRiposoDisponibile = (idx) => {
    const arr = [...(prefForm.giorni_riposo_disponibili || [4, 5, 6])];
    const i = arr.indexOf(idx);
    if (i >= 0) arr.splice(i, 1);
    else arr.push(idx);
    arr.sort((a, b) => a - b);
    setPrefForm({ ...prefForm, giorni_riposo_disponibili: arr });
  };

  const toggleTurnoEscluso = (fasciaId) => {
    const arr = [...(prefForm.turni_esclusi || [])];
    const i = arr.indexOf(fasciaId);
    if (i >= 0) arr.splice(i, 1);
    else arr.push(fasciaId);
    setPrefForm({ ...prefForm, turni_esclusi: arr });
  };

  // ============ CONTRATTO ============
  
  const handleTipoContrattoChange = (value) => {
    const tipo = TIPI_CONTRATTO.find(t => t.value === value);
    if (tipo) {
      setContrattoForm(prev => ({
        ...prev,
        tipo_contratto: value,
        ore_settimanali: tipo.ore,
        giorni_ferie_annuali: tipo.ferie,
        giorni_ferie_residui: prev.giorni_ferie_residui || tipo.ferie
      }));
    }
  };

  const handleSaveContratto = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/utenti/${operatoreSelezionato.id}`, {
        nome: operatoreSelezionato.nome,
        ruolo: operatoreSelezionato.ruolo,
        tipo_contratto: contrattoForm.tipo_contratto,
        ore_settimanali: contrattoForm.ore_settimanali,
        giorni_ferie_annuali: contrattoForm.giorni_ferie_annuali,
        giorni_ferie_residui: contrattoForm.giorni_ferie_residui,
        data_assunzione: contrattoForm.data_assunzione || null,
        data_fine_contratto: contrattoForm.data_fine_contratto || null,
        note_contratto: contrattoForm.note_contratto,
        stipendio_mensile_netto: contrattoForm.stipendio_mensile_netto,
        tredicesima_in_busta: contrattoForm.tredicesima_in_busta || false
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Contratto aggiornato");
      setDialogContratto(false);
      fetchData();
    } catch (error) {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  // === DOCUMENTI ===
  const openDocumenti = async (operatore) => {
    setOperatoreSelezionato(operatore);
    setDialogDocumenti(true);
    setLoadingDocumenti(true);
    setShowNuovoDocForm(false);
    setNuovoDocForm({ nome: "", data_documento: "", data_scadenza: "", note: "" });
    
    try {
      const res = await axios.get(`${API}/documenti?dipendente_id=${operatore.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocumentiOperatore(res.data);
    } catch (error) {
      toast.error("Errore nel caricamento documenti");
    } finally {
      setLoadingDocumenti(false);
    }
  };

  const handleCreaDocumento = async () => {
    if (!nuovoDocForm.nome.trim()) {
      toast.error("Inserisci il nome del documento");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/documenti`, {
        categoria: "dipendente",
        nome: nuovoDocForm.nome,
        data_documento: nuovoDocForm.data_documento || null,
        data_scadenza: nuovoDocForm.data_scadenza || null,
        note: nuovoDocForm.note || null,
        dipendente_id: operatoreSelezionato.id
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      setDocumentiOperatore(prev => [res.data, ...prev]);
      setShowNuovoDocForm(false);
      setNuovoDocForm({ nome: "", data_documento: "", data_scadenza: "", note: "" });
      toast.success("Documento creato");
    } catch (error) {
      toast.error("Errore nella creazione");
    } finally {
      setSaving(false);
    }
  };

  const handleUploadFileDoc = async (e, docId) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File troppo grande (max 10MB)");
      return;
    }
    
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        await axios.post(`${API}/documenti/${docId}/file`, {
          file_data: reader.result,
          file_name: file.name
        }, { headers: { Authorization: `Bearer ${token}` } });
        
        // Aggiorna lista
        const res = await axios.get(`${API}/documenti?dipendente_id=${operatoreSelezionato.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDocumentiOperatore(res.data);
        toast.success("File caricato");
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error("Errore nel caricamento");
    }
  };

  const handleDeleteDoc = async (docId) => {
    try {
      await axios.delete(`${API}/documenti/${docId}`, { headers: { Authorization: `Bearer ${token}` } });
      setDocumentiOperatore(prev => prev.filter(d => d.id !== docId));
      toast.success("Documento eliminato");
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  // === CONTRATTI ALLEGATI ===
  const fetchContrattiAllegati = async (dipId) => {
    setLoadingContratti(true);
    try {
      const res = await axios.get(`${API}/contratti`, {
        params: { dipendente_id: dipId },
        headers: { Authorization: `Bearer ${token}` }
      });
      setContrattiAllegati(res.data);
    } catch {
      setContrattiAllegati([]);
    } finally {
      setLoadingContratti(false);
    }
  };

  const openContratto = (operatore) => {
    setOperatoreSelezionato(operatore);
    setContrattoForm({
      tipo_contratto: operatore.tipo_contratto || "full_time",
      ore_settimanali: operatore.ore_settimanali || 40,
      giorni_ferie_annuali: operatore.giorni_ferie_annuali || 26,
      giorni_ferie_residui: operatore.giorni_ferie_residui ?? operatore.giorni_ferie_annuali ?? 26,
      data_assunzione: operatore.data_assunzione || "",
      data_fine_contratto: operatore.data_fine_contratto || "",
      note_contratto: operatore.note_contratto || "",
      stipendio_mensile_netto: operatore.stipendio_mensile_netto ?? null,
      tredicesima_in_busta: operatore.tredicesima_in_busta || false
    });
    fetchContrattiAllegati(operatore.id);
    setDialogContratto(true);
  };

  const handleUploadContratto = async (data) => {
    try {
      await axios.post(`${API}/contratti`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Contratto allegato");
      fetchContrattiAllegati(operatoreSelezionato.id);
    } catch {
      toast.error("Errore nell'upload del contratto");
    }
  };

  const handleDeleteContratto = async (contrattoId) => {
    try {
      await axios.delete(`${API}/contratti/${contrattoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Allegato eliminato");
      setContrattiAllegati(prev => prev.filter(c => c.id !== contrattoId));
    } catch {
      toast.error("Errore nell'eliminazione");
    }
  };

  // Buste Paga
  const openBustePaga = (operatore) => {
    setOperatoreSelezionato(operatore);
    setDialogBustePaga(true);
  };

  // Helpers
  const getTipoContratto = (value) => TIPI_CONTRATTO.find(t => t.value === value) || { label: value || "Non specificato", color: "bg-gray-500" };
  const getRuoloInfo = (ruolo) => RUOLI_INFO[ruolo] || { label: ruolo, color: "bg-muted text-muted-foreground", icon: "bg-muted text-muted-foreground", dot: "bg-muted-foreground" };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      return new Date(dateStr).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="loading-state">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="schede-personale-page">
      {/* Header */}
      <div>
        <h1 className="page-title flex items-center gap-2">
          <Users className="h-6 w-6 text-primary" />
          Schede personale
        </h1>
        <p className="page-subtitle">
          Gestisci contratti, turni e ore del personale • {operatori.length} operatori
        </p>
      </div>

      {/* Navigazione Settimane */}
      <div className="flex items-center justify-center gap-3 py-3 mb-4 bg-card rounded-xl border border-border">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setOffsetSettimana(prev => prev - 1)}
          className="text-foreground"
        >
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <div className="text-center min-w-[160px]">
          <p className="text-sm font-medium text-foreground">
            {offsetSettimana === 0 ? "Settimana corrente" : 
             offsetSettimana === -1 ? "Settimana scorsa" : 
             offsetSettimana === 1 ? "Prossima settimana" : 
             `${Math.abs(offsetSettimana)} sett. ${offsetSettimana < 0 ? "fa" : "avanti"}`}
          </p>
          <p className="text-xs text-muted-foreground">{settimanaCorrente.label}</p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setOffsetSettimana(prev => prev + 1)}
          className="text-foreground"
          disabled={offsetSettimana >= 0}
        >
          <ChevronRight className="h-5 w-5" />
        </Button>
        {offsetSettimana !== 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOffsetSettimana(0)}
            className="ml-2 text-xs border-border text-foreground hover:bg-accent hover:text-accent-foreground"
          >
            Oggi
          </Button>
        )}
      </div>

      {/* Lista Operatori */}
      {operatori.length === 0 ? (
        <div className="empty-state">
          <Users className="empty-state-icon" />
          <h3 className="section-title">Nessun operatore</h3>
          <p className="text-muted-custom mt-1">
            Aggiungi operatori dalla sezione Amministrazione → Utenti
          </p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {operatori.map(operatore => {
            const tipoContratto = getTipoContratto(operatore.tipo_contratto);
            const ruoloInfo = getRuoloInfo(operatore.ruolo);
            const oreSettimana = oreSettimanaliPerOperatore[operatore.id] || 0;
            const pref = preferenze[operatore.id];
            const giorniRiposo = pref?.giorni_riposo || 1;
            const giorniDisp = pref?.giorni_riposo_disponibili || [];
            
            return (
              <Card key={operatore.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                {/* Header con nome e ruolo */}
                <div className="p-4 border-b border-border bg-muted/30">
                  <div className="flex items-center gap-3">
                    <div className={cn("w-12 h-12 rounded-xl flex items-center justify-center", ruoloInfo.icon)}>
                      <Users className="h-6 w-6" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-foreground truncate">
                        {operatore.nome || operatore.email || operatore.telefono}
                      </h3>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="secondary" className={ruoloInfo.color}>
                          {ruoloInfo.label}
                        </Badge>
                        {operatore.telefono && (
                          <span className="text-xs text-muted-foreground">{operatore.telefono}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <CardContent className="p-4 space-y-4">
                  
                  {/* ORE SETTIMANA - Con confronto lavorate/programmate */}
                  <div className="p-4 rounded-xl bg-gradient-to-r from-violet-500/10 to-indigo-500/10 border border-violet-500/20">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground">Ore settimana</p>
                        <div className="flex items-baseline gap-1">
                          <span className={`text-3xl font-bold ${
                            (oreLavorate[operatore.id] || 0) > oreSettimana 
                              ? 'text-amber-500' 
                              : (oreLavorate[operatore.id] || 0) < oreSettimana 
                                ? 'text-red-400' 
                                : 'text-violet-600'
                          }`}>
                            {oreLavorate[operatore.id] || 0}
                          </span>
                          <span className="text-lg text-muted-foreground">/</span>
                          <span className="text-lg text-muted-foreground">{oreSettimana}h</span>
                        </div>
                        {(oreLavorate[operatore.id] || 0) > oreSettimana && (
                          <p className="text-xs text-amber-500 mt-1">
                            +{((oreLavorate[operatore.id] || 0) - oreSettimana).toFixed(1)}h straordinario
                          </p>
                        )}
                        {(oreLavorate[operatore.id] || 0) < oreSettimana && oreSettimana > 0 && (
                          <p className="text-xs text-red-400 mt-1">
                            -{(oreSettimana - (oreLavorate[operatore.id] || 0)).toFixed(1)}h mancanti
                          </p>
                        )}
                      </div>
                      <Clock className="h-8 w-8 text-violet-500/50" />
                    </div>
                  </div>

                  {/* Griglia: Contratto + Preferenze Turni */}
                  <div className="grid grid-cols-2 gap-3">
                    {/* Contratto */}
                    <button
                      onClick={() => openContratto(operatore)}
                      className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20 text-left hover:bg-blue-500/20 transition-colors"
                    >
                      <div className="flex items-center gap-2 text-blue-600 mb-1">
                        <Briefcase className="h-4 w-4" />
                        <span className="text-xs font-medium">Contratto</span>
                        <Pencil className="h-3 w-3 ml-auto opacity-50" />
                      </div>
                      <p className="text-sm font-bold text-foreground truncate">
                        {tipoContratto.label}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {operatore.ore_settimanali || 40}h/sett
                      </p>
                    </button>

                    {/* Preferenze Turni */}
                    <button
                      onClick={() => openPreferenze(operatore)}
                      className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-left hover:bg-amber-500/20 transition-colors"
                    >
                      <div className="flex items-center gap-2 text-amber-600 mb-1">
                        <Settings className="h-4 w-4" />
                        <span className="text-xs font-medium">Turni</span>
                        <Pencil className="h-3 w-3 ml-auto opacity-50" />
                      </div>
                      <p className="text-sm font-bold text-foreground">
                        {giorniRiposo} riposo/sett
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {giorniDisp.length > 0 ? giorniDisp.map(g => GIORNI_SHORT[g]).join("/") : "Non impostato"}
                      </p>
                    </button>
                  </div>

                  {/* Ferie */}
                  <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                    <div className="flex items-center gap-2">
                      <Sun className="h-4 w-4 text-amber-500" />
                      <span className="text-sm text-muted-foreground">Ferie residue</span>
                    </div>
                    <span className="text-lg font-bold text-foreground">
                      {operatore.giorni_ferie_residui ?? operatore.giorni_ferie_annuali ?? 26}
                      <span className="text-sm font-normal text-muted-foreground ml-1">gg</span>
                    </span>
                  </div>

                  {/* Date contrattuali */}
                  {(operatore.data_assunzione || operatore.data_fine_contratto) && (
                    <div className="pt-3 border-t border-border space-y-2 text-sm">
                      {operatore.data_assunzione && (
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-emerald-500" />
                          <span className="text-muted-foreground">Assunzione:</span>
                          <span className="font-medium text-foreground">{formatDate(operatore.data_assunzione)}</span>
                        </div>
                      )}
                      {operatore.data_fine_contratto && (
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-red-500" />
                          <span className="text-muted-foreground">Scadenza:</span>
                          <span className="font-medium text-foreground">{formatDate(operatore.data_fine_contratto)}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Contatti */}
                  {(operatore.email || operatore.telefono) && (
                    <div className="pt-3 border-t border-border space-y-2 text-sm">
                      {operatore.telefono && (
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-muted-foreground" />
                          <span className="text-foreground">{operatore.telefono}</span>
                        </div>
                      )}
                      {operatore.email && (
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          <span className="text-foreground truncate">{operatore.email}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Note contratto */}
                  {operatore.note_contratto && (
                    <div className="pt-3 border-t border-border">
                      <div className="flex items-start gap-2 text-sm">
                        <FileText className="h-4 w-4 text-muted-foreground mt-0.5" />
                        <p className="text-muted-foreground italic">{operatore.note_contratto}</p>
                      </div>
                    </div>
                  )}

                  {/* Pulsanti Documenti e Buste Paga */}
                  <div className="pt-3 border-t border-border grid grid-cols-2 gap-2">
                    <Button
                      variant="outline"
                      className="gap-2 text-sm"
                      onClick={() => openDocumenti(operatore)}
                      data-testid={`documenti-btn-${operatore.id}`}
                    >
                      <FolderOpen className="h-4 w-4" />
                      Documenti
                    </Button>
                    <Button
                      variant="outline"
                      className="gap-2 text-sm"
                      onClick={() => openBustePaga(operatore)}
                      data-testid={`buste-paga-btn-${operatore.id}`}
                    >
                      <FileText className="h-4 w-4" />
                      Buste Paga
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Dialog Preferenze Turni */}
      <PreferenzeTurniDialog
        open={dialogPreferenze}
        onOpenChange={setDialogPreferenze}
        operatoreSelezionato={operatoreSelezionato}
        prefForm={prefForm}
        setPrefForm={setPrefForm}
        onSave={handleSavePreferenze}
        saving={saving}
        fasce={fasce}
        toggleGiornoRiposoDisponibile={toggleGiornoRiposoDisponibile}
        toggleTurnoEscluso={toggleTurnoEscluso}
      />

      {/* Dialog Contratto */}
      <ContrattoDialog
        open={dialogContratto}
        onOpenChange={setDialogContratto}
        operatoreSelezionato={operatoreSelezionato}
        contrattoForm={contrattoForm}
        setContrattoForm={setContrattoForm}
        onTipoContrattoChange={handleTipoContrattoChange}
        onSave={handleSaveContratto}
        saving={saving}
        contrattiAllegati={contrattiAllegati}
        loadingContratti={loadingContratti}
        onUploadContratto={handleUploadContratto}
        onDeleteContratto={handleDeleteContratto}
        API={API}
      />

      {/* Dialog Documenti */}
      <DocumentiDialog
        open={dialogDocumenti}
        onOpenChange={setDialogDocumenti}
        operatoreSelezionato={operatoreSelezionato}
        showNuovoDocForm={showNuovoDocForm}
        setShowNuovoDocForm={setShowNuovoDocForm}
        nuovoDocForm={nuovoDocForm}
        setNuovoDocForm={setNuovoDocForm}
        onCreaDocumento={handleCreaDocumento}
        saving={saving}
        loadingDocumenti={loadingDocumenti}
        documentiOperatore={documentiOperatore}
        onUploadFileDoc={handleUploadFileDoc}
        onDeleteDoc={handleDeleteDoc}
        API={API}
      />

      {/* Dialog Buste Paga */}
      <BustePagaDialog
        open={dialogBustePaga}
        onOpenChange={setDialogBustePaga}
        operatore={operatoreSelezionato}
        token={token}
      />
    </div>
  );
};

export default SchedePersonale;
